import React from "react";
import { useSelector } from "react-redux";
import { RootState } from "../../../../store";
import { formatDateToReadable, formatDateToReadableWithDay } from "../../../../utils/documentHelper";

export type StageStatus =
  | "submitted"
  | "in-progress"
  | "admin-complete"
  | "reopened"
  | "resubmitted";

export interface StageDisplayProps {
  stageNumber: number;
  status: StageStatus;
  submittedDate?: string;
  startedDate?: string;
  /** Date when the stage was reopened (from candidate.profile.reopened_at) */
  reopenedDate?: string;
  onReopenStage?: () => void;
  showReopenLink?: boolean;
}

/**
 * StageDisplay Component
 *
 * Displays a single stage with its status and relevant dates.
 *
 * Status Flow:
 * - Normal Flow: pending → waiting → approved (submitted) → completed
 * - Reopening Flow: approved → reopened → waiting (after resubmission) → approved
 * - Multiple Stages: approved → reopened (stages: [1, 2]) → waiting → approved
 *
 * When a stage is reopened:
 * - Status shows "Reopened and Pending Submission"
 * - Displays "Reopened [date]" below
 * - Text appears in red to indicate action is required
 *
 * When a stage is resubmitted after reopening:
 * - Status changes to "In Progress and Not Submitted Yet"
 * - Shows "Stage Reopened [date]" if reopenedDate is provided
 *
 * @example
 * ```tsx
 * // For a reopened stage:
 * const isReopened = isStageReopened(candidate, 1);
 * <StageDisplay
 *   stageNumber={1}
 *   status={candidate.profile.review_status === 'reopened' && isReopened ? 'reopened' : 'in-progress'}
 *   reopenedDate={candidate.profile.reopened_at}
 *   startedDate={candidate.profile.created_at}
 * />
 * ```
 */
const StageDisplay: React.FC<StageDisplayProps> = ({
  stageNumber,
  status,
  submittedDate,
  startedDate,
  reopenedDate,
  onReopenStage,
  showReopenLink = true,
}) => {
  const reopenedStagesStatus = useSelector(
    (state: RootState) => state.superadmin.reopenedStagesStatus,
  );
  const renderStatusContent = () => {
    switch (status) { 
      case "admin-complete": 
        return (
          <div className="d-flex flex-column gap-1">
            <div className="fs-13 fw-700 text-grey-medium">Admin To Complete</div>
            <div className="fs-13 fw-400 text-grey-medium">Date Started {formatDateToReadableWithDay(startedDate || "")}</div>
            {onReopenStage && (
              <div
                className="fs-13 fw-700 text-teal-dark cursor-pointer text-decoration-underline"
                onClick={onReopenStage}
                role="button"
                tabIndex={0}
                onKeyPress={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    onReopenStage();
                  }
                }}
              >
                Reopen Stage
              </div>
            )}
          </div>

        );

      case "reopened":
        return (
          <div className="d-flex flex-column gap-1">
            <div className="fs-13 fw-700 text-alert">
              In Progress and Not Submitted Yet
            </div>
            {reopenedDate && (
              <div className="fs-13 fw-400 text-alert">
                Stage Reopened {formatDateToReadableWithDay(reopenedDate)}
              </div>
            )}
          </div>
        );

      case "resubmitted":
        return (
          <div className="d-flex flex-column gap-1">
            <div className="fs-13 fw-400 text-grey-medium">
              Resubmitted {submittedDate && formatDateToReadableWithDay(submittedDate)}
            </div>
            {showReopenLink && onReopenStage && (
              <div
                className="fs-13 fw-700 text-teal-dark cursor-pointer text-decoration-underline"
                onClick={onReopenStage}
                role="button"
                tabIndex={0}
                onKeyPress={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    onReopenStage();
                  }
                }}
              >
                Reopen Stage
              </div>
            )}
          </div>
        );

      case "submitted":
        return (
          <div className="d-flex flex-column gap-1">
            <div className="fs-13 fw-400 text-grey-medium">
              Submitted {submittedDate && formatDateToReadableWithDay(submittedDate)}
            </div>
            {showReopenLink && onReopenStage && (
              <div
                className="fs-13 fw-700 text-teal-dark cursor-pointer text-decoration-underline"
                onClick={onReopenStage}
                role="button"
                tabIndex={0}
                onKeyPress={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    onReopenStage();
                  }
                }}
              >
                Reopen Stage
              </div>
            )}
          </div>
        );

      case "in-progress":
      default:
        return (
          <div className="d-flex flex-column gap-1">
            <div className="fs-13 fw-700 text-alert">
              In Progress and Not Submitted Yet
            </div>
            {reopenedDate ? (
              <div className="fs-13 fw-400 text-alert">
                Stage Reopened {formatDateToReadableWithDay(reopenedDate)}
              </div>
            ) : startedDate ? (
              <div className="fs-13 fw-400 text-alert">
                Date Started {formatDateToReadable(startedDate)}
              </div>
            ) : null}
          </div>
        );
    }
  };

  return (
    <div className="d-flex gap-10 align-items-center">
      <div className="bg-teal-muted border-1 border-solid border-black radius-8px min-w-70px text-center fs-13 fw-400 lh-22px">
        Stage {stageNumber}
      </div>
      {renderStatusContent()}
    </div>
  );
};

export default StageDisplay;
